/**
 * ProfileViewsEnhanced.tsx
 *
 * File-level:
 * - Enhanced dashboard profile views component
 * - Fetches combined summary endpoint and persistent notifications
 * - Displays unique viewers, total views, a sparkline for recent trend and an in-app notification dropdown
 *
 * Notes:
 * - Uses Tailwind CSS and existing ui components.
 * - Uses fetchJSON helper to ensure requests go to configured API base (avoids /api 404s when backend runs on a different port).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Users, Bell, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { fetchJSON } from '@/utils/api';
import { formatStoredDate } from '@/utils/formatStoredDate';

/**
 * Props for ProfileViewsEnhanced component
 */
interface ProfileViewsEnhancedProps {
  /**
   * Optional profileId to fetch for. If not provided the component will try to read userData from localStorage.
   */
  profileId?: string;
  /**
   * Trend window days (default 14)
   */
  days?: number;
  /**
   * Poll interval in ms (default 20000)
   */
  pollInterval?: number;
}

/**
 * Notification shape returned from backend
 */
interface NotificationItem {
  id: number;
  userId: string;
  title?: string;
  message?: string;
  type?: string | null;
  metadata?: string | null;
  isRead: boolean;
  createdAt?: string;
  readAt?: string | null;
}

/**
 * Summary shape returned by /dashboard/profile-views/summary/:profileId
 */
interface SummaryResponse {
  profileId: string;
  profileCount: number;
  viewStats: {
    totalViews: number;
    uniqueViewers: number;
    lastViewedAt?: string | null;
  };
  trend: { date: string; count: number }[];
}

/**
 * Utility: get profileId from props or localStorage userData
 * @param provided optional profileId prop
 */
function resolveProfileIdFromStorage(provided?: string) {
  if (provided && String(provided).trim().length > 0) return String(provided).trim();
  try {
    const stored = typeof window !== 'undefined' ? localStorage.getItem('userData') : null;
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return String(parsed?.profileId ?? parsed?.userId ?? parsed?.id ?? parsed?.profile?.id ?? '').trim() || null;
  } catch {
    return null;
  }
}

/**
 * Build a lightweight sparkline path from array of numbers
 * @param points numbers
 * @param w width
 * @param h height
 * @returns svg path d
 */
function buildSparklinePath(points: number[], w = 140, h = 36) {
  if (!points || points.length === 0) return '';
  const max = Math.max(...points, 1);
  const min = Math.min(...points, 0);
  const range = Math.max(max - min, 1);
  const step = w / (points.length - 1 || 1);
  let d = '';
  points.forEach((p, i) => {
    const x = Math.round(i * step);
    const y = Math.round(h - ((p - min) / range) * h);
    d += i === 0 ? `M ${x} ${y}` : ` L ${x} ${y}`;
  });
  return d;
}

/**
 * ProfileViewsEnhanced component
 *
 * - Responsible for fetching combined summary (stats + trend) and notifications
 * - Shows sparkline, counts and a notifications bell with dropdown
 */
export default function ProfileViewsEnhanced({ profileId: profileIdProp, days = 14, pollInterval = 20000 }: ProfileViewsEnhancedProps) {
  const [profileId, setProfileId] = useState<string | null>(() => resolveProfileIdFromStorage(profileIdProp));
  const [loading, setLoading] = useState<boolean>(true);
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [cached, setCached] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [notificationsOpen, setNotificationsOpen] = useState<boolean>(false);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [notifLoading, setNotifLoading] = useState<boolean>(false);
  const [notifError, setNotifError] = useState<string | null>(null);

  // Track last known unique viewers in localStorage to show in-app indicator
  const lastKnownKey = `pv_last_known_unique_${profileId || 'unknown'}`;

  /**
   * Fetch summary using fetchJSON helper so API base is honored
   */
  async function fetchSummary() {
    if (!profileId) return;
    setError(null);
    setLoading(true);
    try {
      const json = await fetchJSON(`/dashboard/profile-views/summary/${encodeURIComponent(profileId)}?days=${days}`);
      if (!json || !json.success || !json.data) {
        setError(json?.message || 'Failed to fetch summary');
        setLoading(false);
        return;
      }
      setSummary(json.data as SummaryResponse);
      setCached(Boolean(json.cached));
      setError(null);

      // Update local last-known unique viewers (no UI toast here; backend notifications drive persistent notifications)
      try {
        const last = Number(localStorage.getItem(lastKnownKey) || 0);
        const current = Number(json.data.viewStats.uniqueViewers || 0);
        if (current > last) {
          localStorage.setItem(lastKnownKey, String(current));
        } else if (!localStorage.getItem(lastKnownKey)) {
          localStorage.setItem(lastKnownKey, String(current));
        }
      } catch {
        // ignore
      }
    } catch (e) {
      setError('Network error while fetching summary');
    } finally {
      setLoading(false);
    }
  }

  /**
   * Fetch unread count for notifications
   */
  async function fetchUnreadCount() {
    if (!profileId) return;
    try {
      const json = await fetchJSON(`/dashboard/profile-views/notifications/unread-count/${encodeURIComponent(profileId)}`);
      if (json && json.success && json.data) {
        setUnreadCount(Number(json.data.unreadCount || 0));
      }
    } catch {
      // ignore silently
    }
  }

  /**
   * Fetch notifications list for the profileId
   * @param onlyUnread optional
   */
  async function fetchNotifications(onlyUnread = false) {
    if (!profileId) return;
    setNotifLoading(true);
    setNotifError(null);
    try {
      const json = await fetchJSON(`/dashboard/profile-views/notifications/${encodeURIComponent(profileId)}?limit=50&onlyUnread=${onlyUnread ? 'true' : 'false'}`);
      if (!json || !json.success || !json.data) {
        setNotifError(json?.message || 'Failed to fetch notifications');
        setNotifications([]);
      } else {
        setNotifications(json.data.notifications || []);
      }
    } catch (e) {
      setNotifError('Network error while fetching notifications');
      setNotifications([]);
    } finally {
      setNotifLoading(false);
    }
  }

  /**
   * Mark a notification read
   * @param id notificationId
   */
  async function markRead(id: number) {
    if (!profileId) return;
    try {
      await fetchJSON(`/dashboard/profile-views/notifications/mark-read`, {
        method: 'POST',
        body: JSON.stringify({ userId: profileId, notificationId: id })
      });
      await fetchNotifications(false);
      await fetchUnreadCount();
    } catch {
      // ignore UI-level error
    }
  }

  // Initial fetch + polling
  useEffect(() => {
    setProfileId(resolveProfileIdFromStorage(profileIdProp));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileIdProp]);

  useEffect(() => {
    if (!profileId) return;
    fetchSummary();
    fetchUnreadCount();
    const id = setInterval(() => {
      fetchSummary();
      fetchUnreadCount();
    }, pollInterval);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId, days, pollInterval]);

  // When notifications panel opens, load notifications
  useEffect(() => {
    if (notificationsOpen && profileId) {
      fetchNotifications(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationsOpen, profileId]);

  // Prepare sparkline points from summary.trend
  const sparklinePath = useMemo(() => {
    if (!summary || !Array.isArray(summary.trend)) return '';
    const points = summary.trend.map(t => Number(t.count || 0));
    return buildSparklinePath(points, 140, 36);
  }, [summary]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-slate-700" />
            <div>
              <CardTitle className="text-sm">Profile Views</CardTitle>
              <div className="text-xs text-gray-500">Unique viewers & recent trend</div>
            </div>
            {cached && <span className="ml-2 text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">cached</span>}
          </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <Button size="sm" className="bg-transparent border-gray-200 text-gray-700" onClick={() => setNotificationsOpen(prev => !prev)}>
                <Bell className="w-4 h-4" />
              </Button>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-medium leading-none text-white bg-red-600 rounded-full">
                  {unreadCount}
                </span>
              )}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-2xl font-bold text-slate-900">
              {loading ? '—' : (summary ? String(summary.viewStats.uniqueViewers) : '—')}
            </div>
            <div className="text-xs text-gray-500">Unique viewers</div>
          </div>

          <div className="text-right">
            <div className="text-lg font-medium text-slate-700">
              {loading ? '—' : (summary ? String(summary.viewStats.totalViews) : '—')}
            </div>
            <div className="text-xs text-gray-500">Total views</div>
          </div>
        </div>

        <div className="mb-3">
          <div className="text-xs text-gray-500 mb-2">Last {days} days</div>
          <div className="w-full h-10">
            {sparklinePath ? (
              <svg width="100%" height="36" viewBox="0 0 140 36" preserveAspectRatio="none" className="block">
                <path d={sparklinePath} fill="none" stroke="#0ea5e9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : (
              <div className="text-xs text-gray-400">No trend data</div>
            )}
          </div>
        </div>

        {/* Notifications dropdown */}
        {notificationsOpen && (
          <div className="mt-2 border rounded bg-white p-3 shadow max-h-64 overflow-auto">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold">Notifications</div>
              <div className="flex items-center gap-2">
                <Button size="sm" className="bg-transparent border-gray-200 text-gray-700" onClick={() => fetchNotifications(false)}>
                  Refresh
                </Button>
                <Button size="sm" className="bg-transparent border-gray-200 text-gray-700" onClick={() => setNotificationsOpen(false)}>
                  Close
                </Button>
              </div>
            </div>

            {notifLoading ? (
              <div className="text-sm text-gray-500">Loading...</div>
            ) : notifError ? (
              <div className="text-sm text-red-600">{notifError}</div>
            ) : notifications.length === 0 ? (
              <div className="text-sm text-gray-500">No notifications</div>
            ) : (
              <ul className="space-y-2">
                {notifications.map(n => (
                  <li key={n.id} className="flex items-start justify-between p-2 bg-slate-50 rounded">
                    <div>
                      <div className="text-sm font-medium text-slate-800">{n.title || 'Notification'}</div>
                      <div className="text-xs text-gray-600">{n.message}</div>
                      <div className="text-xs text-gray-400 mt-1">{n.createdAt ? formatStoredDate(n.createdAt) : ''}</div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {!n.isRead ? (
                        <Button size="sm" className="bg-green-600 text-white" onClick={() => markRead(n.id)}>
                          <Check className="w-3 h-3 mr-1 inline" /> Mark
                        </Button>
                      ) : (
                        <span className="text-xs text-gray-500">Read</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
} 